import { Link, type LinkProps } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

export function BackLink({ to, label }: { to: LinkProps["to"]; label: string }) {
  return (
    <Link to={to} className="inline-flex w-fit items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground">
      <ArrowLeft className="size-3.5" aria-hidden />
      {label}
    </Link>
  );
}

export interface Stat {
  value: string | number;
  label: string;
}

export function StatStrip({ stats, className }: { stats: Stat[]; className?: string }) {
  return (
    <div className={cn("flex flex-wrap gap-x-6 gap-y-2", className)}>
      {stats.map((s) => (
        <div key={s.label} className="flex flex-col">
          <span className="block font-display text-xl font-medium leading-none tabular-nums text-foreground">
            {s.value}
          </span>
          <span className="mt-1 block text-[13px] text-muted-foreground">{s.label}</span>
        </div>
      ))}
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  /** Second phrase set in italic, e.g. title="Saved" emphasis="restaurants". */
  emphasis?: string;
  description?: string;
  stats?: Stat[];
  actions?: React.ReactNode;
  backTo?: { to: LinkProps["to"]; label: string };
  children?: React.ReactNode;
}

export function PageHeader({ title, emphasis, description, stats, actions, backTo, children }: PageHeaderProps) {
  return (
    <header className="flex flex-col gap-3">
      {backTo && <BackLink to={backTo.to} label={backTo.label} />}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="font-display text-[34px] font-medium leading-[1.05] tracking-[-0.02em] text-brand">
            {title}
            {emphasis && <em className="font-normal italic"> {emphasis}</em>}
          </h1>
          {description && <p className="mt-1 text-[13.5px] text-muted-foreground">{description}</p>}
          {children}
        </div>
        {stats && stats.length > 0 && <StatStrip stats={stats} />}
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
